import type { RCard } from 'v2/types/api/billing'
import { useAccountDetailsQuery, useBillingBalanceQuery, useBillingCardsQuery } from '../queries'
import { CardState } from '../types'
import type { CardEligibilityContext } from './card-eligibility'

/**
 * Feeds the eligibility rules from the account, balance and card queries.
 *
 * Held here, beside `CardRowActions`, so every surface that renders the row menu
 * gets the same answer. Previously the Payment methods page computed this
 * inline and the card picker in the recharge modals — which shows the same menu —
 * computed nothing at all.
 *
 * All three queries are already in flight elsewhere on these screens, so this is
 * a cache read rather than extra network.
 */
export const useCardEligibility = (card: RCard) => {
  const { data: accountDetails, isLoading: isAccountLoading } = useAccountDetailsQuery()
  const { data: balance, isLoading: isBalanceLoading } = useBillingBalanceQuery()
  const { data: cards } = useBillingCardsQuery()

  const context: CardEligibilityContext = {
    // `!!accountDetails` rather than `!isAccountLoading`: a settled-but-failed
    // query is not loading yet has no data, and treating that as "loaded" would
    // let the autopay check run on absent information.
    accountLoaded: !isAccountLoading && !!accountDetails,
    // Mirrors the page's `isQueriesLoading` — in flight, not merely dataless.
    queriesLoading: isAccountLoading || isBalanceLoading,
    currency: accountDetails?.account?.currency,
    autoRecharge: balance?.auto_recharge,
  }

  // Replacements the confirm dialog can offer — every other active card.
  const otherActiveCards = (cards ?? []).filter(
    (c) => c.card_mapping_id !== card.card_mapping_id && c.state === CardState.ACTIVE,
  )

  return { context, otherActiveCards }
}
